import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaCalendar, FaUser, FaArrowLeft, FaShareAlt } from 'react-icons/fa';
import type { News } from '../api/types';
import { getNewsById, getNews } from '../api/news';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
`;

const HeroSection = styled.section`
  position: relative;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
  color: white;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  text-align: center;
`;

const BackButton = styled.button`
  position: absolute;
  top: ${props => props.theme.spacing.xl};
  left: ${props => props.theme.spacing.xl};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    top: ${props => props.theme.spacing.md};
    left: ${props => props.theme.spacing.md};
    font-size: 0.9rem;
  }
`;

const CategoryBadge = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Title = styled(motion.h1)`
  font-size: 2.5rem;
  margin: ${props => props.theme.spacing.lg} 0;
  font-weight: 700;
  line-height: 1.3;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.8rem;
  }
`;

const MetaInfo = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing.xl};
  margin-top: ${props => props.theme.spacing.lg};
  font-size: 1rem;
  opacity: 0.95;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.sm};
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const ContentSection = styled.section`
  max-width: 900px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 1;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  }
`;

const ArticleCard = styled(motion.div)`
  background: white;
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.lg};
  padding: ${props => props.theme.spacing.xxl};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.xl};
  }
`;

const ArticleContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.text};

  p {
    margin-bottom: ${props => props.theme.spacing.lg};
  }

  h2 {
    font-size: 1.8rem;
    font-weight: 600;
    margin: ${props => props.theme.spacing.xxl} 0 ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.text};
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: ${props => props.theme.spacing.xl} 0 ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.text};
  }

  ul, ol {
    margin: ${props => props.theme.spacing.lg} 0;
    padding-left: ${props => props.theme.spacing.xl};
  }

  li {
    margin-bottom: ${props => props.theme.spacing.sm};
  }

  blockquote {
    border-left: 4px solid ${props => props.theme.colors.primary};
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
    background: ${props => props.theme.colors.background};
    margin: ${props => props.theme.spacing.lg} 0;
    font-style: italic;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: ${props => props.theme.borderRadius.md};
    margin: ${props => props.theme.spacing.lg} 0;
  }

  code {
    background: ${props => props.theme.colors.background};
    padding: 2px 6px;
    border-radius: ${props => props.theme.borderRadius.sm};
    font-family: monospace;
  }

  pre {
    background: ${props => props.theme.colors.background};
    padding: ${props => props.theme.spacing.md};
    border-radius: ${props => props.theme.borderRadius.md};
    overflow-x: auto;
    margin: ${props => props.theme.spacing.lg} 0;

    code {
      background: none;
      padding: 0;
    }
  }
`;

const ShareSection = styled.div`
  display: flex;
  justify-content: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.xl} 0;
  border-top: 1px solid ${props => props.theme.colors.border};
  margin-top: ${props => props.theme.spacing.xl};
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const RelatedNews = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
  padding: ${props => props.theme.spacing.xl};
`;

const RelatedNewsTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text};
`;

const RelatedNewsList = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.md};
`;

const RelatedNewsItem = styled.div`
  padding: ${props => props.theme.spacing.md};
  border-left: 3px solid ${props => props.theme.colors.primary};
  background: ${props => props.theme.colors.background};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.primaryLight};
    transform: translateX(5px);
  }
`;

const RelatedNewsItemTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const RelatedNewsItemDate = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textLight};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 100px 20px;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textLight};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 100px 20px;
  color: #e74c3c;
  font-size: 1.1rem;
`;

const NewsDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [news, setNews] = useState<News | null>(null);
  const [relatedNews, setRelatedNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNewsDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        // 뉴스 상세 정보 가져오기
        const newsDetail = await getNewsById(Number(id), i18n.language);
        setNews(newsDetail);

        // 관련 뉴스 가져오기 (같은 카테고리의 다른 뉴스)
        const allNews = await getNews({ published: true, lang: i18n.language });
        const related = allNews
          .filter((item: News) => item.id !== Number(id) && item.category === newsDetail.category)
          .slice(0, 3);
        setRelatedNews(related);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('error.serverError'));
        console.error('Failed to fetch news detail:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchNewsDetail();
    }
  }, [id, i18n.language, t]);

  // 날짜 포맷팅 함수 (연월일만 표시)
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return dateString.split('T')[0]; // 2025-10-22T01:43:21.781331 → 2025-10-22
  };

  const handleShare = () => {
    if (navigator.share && news) {
      navigator.share({
        title: news.title,
        text: news.excerpt,
        url: window.location.href,
      }).catch((error) => console.log('Error sharing:', error));
    } else {
      // 공유 API가 지원되지 않으면 URL 복사
      navigator.clipboard.writeText(window.location.href);
      alert(t('success.linkCopied'));
    }
  };

  const handleRelatedNewsClick = (newsId: number) => {
    navigate(`/news/${newsId}`);
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>{t('loading.news')}</LoadingMessage>
      </Container>
    );
  }

  if (error || !news) {
    return (
      <Container>
        <ErrorMessage>{error || t('error.newsNotFound')}</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <HeroSection>
        <BackButton onClick={() => navigate('/news')}>
          <FaArrowLeft />
          {t('common.backToList')}
        </BackButton>

        <CategoryBadge>{news.category}</CategoryBadge>

        <Title
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {news.title}
        </Title>

        <MetaInfo
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <MetaItem>
            <FaCalendar />
            {formatDate(news.publishedAt)}
          </MetaItem>
          <MetaItem>
            <FaUser />
            {news.author || '나우바이오'}
          </MetaItem>
        </MetaInfo>
      </HeroSection>

      <ContentSection>
        <ArticleCard
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <ArticleContent dangerouslySetInnerHTML={{ __html: news.content }} />

          <ShareSection>
            <ShareButton onClick={handleShare}>
              <FaShareAlt />
              {t('common.share')}
            </ShareButton>
          </ShareSection>
        </ArticleCard>

        {relatedNews.length > 0 && (
          <RelatedNews>
            <RelatedNewsTitle>{t('common.relatedNews')}</RelatedNewsTitle>
            <RelatedNewsList>
              {relatedNews.map((item) => (
                <RelatedNewsItem
                  key={item.id}
                  onClick={() => handleRelatedNewsClick(item.id)}
                >
                  <RelatedNewsItemTitle>{item.title}</RelatedNewsItemTitle>
                  <RelatedNewsItemDate>{formatDate(item.publishedAt)}</RelatedNewsItemDate>
                </RelatedNewsItem>
              ))}
            </RelatedNewsList>
          </RelatedNews>
        )}
      </ContentSection>
    </Container>
  );
};

export default NewsDetailPage;
